import { z } from 'zod';
// Meta paid strategy proposal (autopilot phase A5).
//
// Mag reads the campaign's recent meta_paid_ad metrics + brand
// brain + latest organic engagement digest, and proposes 3-5 new
// angles to test next. Each angle is a paid-ad-hook-style concept
// — a specific hook + audience hypothesis + format + budget
// recommendation grounded in actual signal from the campaign's
// history.
//
// Stored on creative_ads campaigns alongside the existing draft
// plan. NOT a discrete campaign kind — strategy is an ongoing
// loop on top of whatever campaign already exists.
//
// Cost: ~$0.10-0.15 per run (one Sonnet call against rich
// context). Founder-triggered ("what should I test next?")
// rather than scheduled — running on every metrics tick would
// be wasteful since the signal-to-noise on a 7d window is low.
export const metaPaidStrategyAngleSchema = z.object({
    /** The hook — what the ad LEADS with. Lowercase, mag voice.
     *  ≤180 chars (paid ads run short). Pulls from voice patterns
     *  in the brand brain when present. */
    hook: z.string().min(1).max(180),
    /** Mag's audience hypothesis — who she thinks this angle will
     *  resonate with + why. One sentence. Should reference brain
     *  customer segments or platform-level audience signals from
     *  the campaign's metrics history. */
    audienceHypothesis: z.string().min(1).max(280),
    /** Format she'd run it as. Matches the existing meta_paid_ad
     *  deliverable's render path. */
    format: z.enum(['static_image', 'carousel', 'reel', 'video']),
    /** Suggested daily budget in USD. Mag picks a conservative
     *  starter — testing budget, not scaling budget. Founder can
     *  bump after launch + metric review. Cap at $50/day for v1
     *  so a stack of approved angles doesn't blow the budget. */
    suggestedDailyBudget: z.number().int().min(5).max(50),
    /** Why mag picked this angle. Cites the actual signal from
     *  the campaign's metric history or brand brain. ≤500 chars,
     *  lowercase. */
    rationale: z.string().min(1).max(500),
});
export const metaPaidStrategyOutputSchema = z.object({
    /** Mag's one-sentence framing of the strategy. ("your saved
     *  reach is doubling weekly, want to lean into the founder-pov
     *  + ICP-pain angles next.") */
    summary: z.string().min(1).max(280),
    /** 3-5 angles to test. Fewer is sharper. Cap at 6. */
    angles: z.array(metaPaidStrategyAngleSchema).min(1).max(6),
    /** ISO timestamp the strategy was generated. Surfaces in the
     *  UI as "proposed N hours ago" + invalidates downstream caches
     *  when the founder re-runs. */
    generatedAt: z.string(),
    /** Cost in USD for transparency. Same surface as the other
     *  agent outputs. */
    costUsd: z.number().min(0),
});
//# sourceMappingURL=meta-paid-strategy.js.map